<style lang="scss" scoped>
  dialog[open] {
    position: fixed;
    top: 0;
    left: 0;
    display: grid;
    grid-template-rows: 1fr 1fr auto;
    width: 100vw;
    height: 100vh;
    padding: 0;
    border: 0;
    text-align: center;
    overflow-y: auto;
    background: var(--grey-lighter);
  }

  .shadow {
    -webkit-box-shadow: 1px 1px 14px 3px rgba(71,71,71,0.05);
    -moz-box-shadow: 1px 1px 14px 3px rgba(71,71,71,0.05);
    box-shadow: 1px 1px 14px 3px rgba(71,71,71,0.05);
  }

  .title-lg {
    align-self: end;
    padding-bottom: var(--spacing);
    color: var(--text);
  }

  .passcode {
    letter-spacing: 0.5em;
    font-family: monospace;
    font-size: 30px;
    color: var(--blue);
  }

  .cancel-button {
    margin-top: 2rem;
    font-family: 'Open Sans', sans-serif;
  }

  .passcode-buttons {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 20px;

    div {
      padding: 10px 0px;
    }

    .btn {
      display: inline-block;
      width: 80px;
      height: 80px;
      background: white;
      border-radius: 50%;
      font-size: 1.7em !important;
      font-weight: 600;

      &:active {
        background-color: var(--green);
      }
    }

    div:last-child {
      .btn {
        background: transparent;
        font-size: 1em !important;
        box-shadow: none;
      }
    }
  }
</style>

<template>
  <dialog
    ref="dialog"
    @cancel.prevent
  >
    <div class="title-lg">
      {{ heading }}
    </div>

    <template v-if="status === 'ready' && unlockMode === 'passcode'">
      <div class="passcode">
        <div>{{ passcodeDisplay }}</div>

        <button
          v-if="mode !== 'promptToUnlockApp'"
          type="button"
          class="btn btn-transparent cancel-button"
          @click="closeModal(true)"
        >
          {{ $t('Calendar.cancel') }}
        </button>
      </div>

      <div class="passcode-buttons">
        <div
          v-for="(buttonValue, index) of buttons"
          :key="`button${index}`"
        >
          <button
            v-if="buttonValue !== ''"
            type="button"
            class="btn btn-transparent shadow"
            @click="buttonHandler(buttonValue)"
          >
            {{ buttonValue }}
          </button>
        </div>
      </div>
    </template>
  </dialog>
</template>

<script>
  export default {
    name: 'SecurityWall',
    props: {
      mode: {
        type: String,
        required: false,
        default: 'promptToUnlockApp',
        validator: value => [
          'promptToUnlockApp',
          'promptToChangePasscode',
          'promptToRemoveSecurityWall',
          'setPasscode',
          'changePasscode',
        ].includes(value),
      },
    },
    data() {
      let status;

      if (this.mode === 'promptToUnlockApp') {
        status = 'loading';
      }
      else {
        status = 'ready';
      }

      return {
        buttons: [
          '1',
          '2',
          '3',
          '4',
          '5',
          '6',
          '7',
          '8',
          '9',
          '',
          '0',
          this.$t('BucketList.delete'),
        ],
        confirmation: false,
        passcode: '',
        passcodeConfirm: '',
        unlockMode: 'passcode',
        status,
      };
    },
    computed: {
      heading() {
        if (this.status === 'loading') {
          return this.$t('SecurityWall.loading');
        }

        if (this.mode === 'promptToChangePasscode') {
          return this.$t('SecurityWall.enter-current-passcode');
        }

        if (this.mode === 'setPasscode') {
          if (this.confirmation) {
            return this.$t('SecurityWall.confirm-passcode');
          }

          return this.$t('SecurityWall.set-passcode');
        }

        if (this.mode === 'changePasscode') {
          if (this.confirmation) {
            return this.$t('SecurityWall.confirm-new-passcode');
          }

          return this.$t('SecurityWall.enter-new-passcode');
        }

        if (
          this.mode === 'promptToUnlockApp' &&
          this.unlockMode === 'biometrics'
        ) {
          return '';
        }

        return this.$t('SecurityWall.enter-passcode');
      },
      passcodeDisplay() {
        let passcode;

        if (this.confirmation) {
          passcode = this.passcodeConfirm;
        }
        else {
          passcode = this.passcode;
        }

        return passcode.replace(/[0-9]/g, '•').padEnd(4, '_');
      },
    },
    created() {
      if (this.mode === 'promptToUnlockApp') {
        if (
          this.$store.state.securityWall.useBiometrics &&
          window.Fingerprint
        ) {
          this.unlockMode = 'biometrics';
          this.status = 'ready';

          window.Fingerprint.isAvailable(
            () => {
              window.Fingerprint.show(
                {
                  description: this.$t('SecurityWall.unlock-to-continue'),
                },
                () => {
                  this.closeModal();
                },
                () => {
                  // Maybe the user dismissed the biometrics prompt.

                  this.unlockMode = 'passcode';
                },
              );
            },
            () => {
              // Maybe sometime after the user enabled biometrics within
              // the app, they went into their device's settings and
              // deleted their fingerprint(s).

              this.unlockMode = 'passcode';
            },
          );
        }
        else {
          this.status = 'ready';
        }
      }
    },
    mounted() {
      window.dialogPolyfill.registerDialog(this.$refs.dialog);
      this.$refs.dialog.showModal();
      document.body.classList.add('security-wall-modal-open');
    },
    methods: {
      buttonHandler(buttonValue) {
        if (this.confirmation) {
          if (buttonValue === this.$t('BucketList.delete')) {
            this.passcodeConfirm = this.passcodeConfirm.slice(0, -1);
          }
          else {
            this.passcodeConfirm += buttonValue;
          }

          if (this.passcodeConfirm.length === 4) {
            if (this.passcode === this.passcodeConfirm) {
              if (this.mode === 'setPasscode') {
                this.$store.commit('securityWall', {
                  enabled: true,
                  passcode: this.passcode,
                  useBiometrics: false,
                });
              }
              else {
                const securityWall = JSON.parse(JSON.stringify(this.$store.state.securityWall));
                securityWall.passcode = this.passcode;
                this.$store.commit('securityWall', securityWall);
              }

              this.closeModal();
            }
            else {
              this.passcodeConfirm = '';
              this.alert(
                this.$t('SecurityWall.the-two-passcodes-you-entered-did-not-match-please-try-again'),
                () => {},
                'Passcode Mismatch'
              );
            }
          }
        }
        else {
          if (buttonValue === this.$t('BucketList.delete')) {
            this.passcode = this.passcode.slice(0, -1);
          }
          else {
            this.passcode += buttonValue;
          }

          if (this.passcode.length === 4) {
            if (['setPasscode', 'changePasscode'].includes(this.mode)) {
              this.confirmation = true;
            }
            else if (this.passcode === this.$store.state.securityWall.passcode) {
              this.passcode = '';

              if (this.mode === 'promptToRemoveSecurityWall') {
                this.$store.commit('securityWall', {
                  enabled: false,
                  passcode: '',
                  useBiometrics: false,
                });
              }

              this.closeModal();
            }
            else {
              this.passcode = '';
              this.alert(this.$t('SecurityWall.incorrect-passcode'), () => {}, 'Access Denied');
            }
          }
        }
      },
      closeModal(canceled = false) {
        this.$refs.dialog.close();
        document.body.classList.remove('security-wall-modal-open');
        this.$emit('closed', canceled);
      },
    },
  };
</script>
